/**
 * Site conditions: what the site weather card says beyond the station's
 * newest reading. Dew point comes from air temperature and humidity; the
 * change over the last hour, the 24-hour low and high, the last hour's wind
 * peak and the 24-hour air temperature trace come from the pour's station
 * readings (GET /pour/{id}/weather). Each window is measured back from the
 * newest reading, not from now, so a station that has gone quiet still
 * describes the hours it did record.
 *
 * Site weather shows only for a linked station: the pour's Weather Station
 * with Onsite Weather Conditions or Concrete Evaporation Rate on. A station
 * that is not linked gets no readings, so nothing is shown for it.
 */
import type { SiteWeather, WeatherSeries } from '../services/pours';

type Point = WeatherSeries['points'][number];

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
/** How far from an hour before the newest reading the comparison reading may be. */
const TREND_TOLERANCE_MS = 20 * MINUTE_MS;

/** The pour's site weather when its station is linked; null otherwise. */
export const linkedSiteWeather = (site?: SiteWeather | null): SiteWeather | null =>
  site && site.linked ? site : null;

/** Dew point (°C) by the Magnus formula (Alduchov and Eskridge constants),
 *  or null without both values or with no humidity. */
export const dewPoint = (tempC?: number | null, rh?: number | null): number | null => {
  if (tempC == null || rh == null || !Number.isFinite(tempC) || !Number.isFinite(rh) || rh <= 0) return null;
  const a = 17.625;
  const b = 243.04;
  const g = Math.log(Math.min(rh, 100) / 100) + (a * tempC) / (b + tempC);
  return (b * g) / (a - g);
};

export interface SiteConditions {
  /** Air temperature at the newest reading minus the one about an hour
   *  before it; null without a reading near that hour. */
  trend1h: number | null;
  /** Lowest and highest air temperature in the 24 hours to the newest reading. */
  low24h: number | null;
  high24h: number | null;
  /** Highest wind (km/h) in the hour to the newest reading. */
  windPeak1h: number | null;
  /** Air temperature over the 24 hours to the newest reading, [ms, °C], oldest first. */
  trace: [number, number][];
}

const EMPTY: SiteConditions = { trend1h: null, low24h: null, high24h: null, windPeak1h: null, trace: [] };

/** The site conditions from the station readings (oldest first). */
export const siteConditions = (points: readonly Point[]): SiteConditions => {
  const temps = points.filter((p): p is [number, number, number | null, number | null] => p[1] !== null);
  if (temps.length === 0) return EMPTY;
  const [newestAt, newestTemp] = temps[temps.length - 1];

  const trace = temps.filter((p) => p[0] >= newestAt - 24 * HOUR_MS).map((p): [number, number] => [p[0], p[1]]);
  const values = trace.map((p) => p[1]);

  const target = newestAt - HOUR_MS;
  let before: [number, number] | null = null;
  for (const p of trace) {
    if (Math.abs(p[0] - target) <= TREND_TOLERANCE_MS && (!before || Math.abs(p[0] - target) < Math.abs(before[0] - target))) {
      before = p;
    }
  }

  const winds = points
    .filter((p) => p[0] >= target && p[0] <= newestAt && p[3] !== null)
    .map((p) => p[3] as number);

  return {
    trend1h: before ? newestTemp - before[1] : null,
    low24h: Math.min(...values),
    high24h: Math.max(...values),
    windPeak1h: winds.length > 0 ? Math.max(...winds) : null,
    trace,
  };
};
